import React from 'react';
import PropTypes from 'prop-types';
import GanttChart from '../../Common/GanttChart/GanttChart';
import { EVENT_DATE_FORMAT, GANTT_LINK_TYPES } from '../../../constants/constants';
import * as types from '../../../constants/actionTypes';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Moment from 'moment';
import ChangeStartDateModal from '../../TaskList/Calendar/ChangeStartDateModal';
import ChangeDaysOutModal from './ChangeDaysOutModal';

export class GanttChartView extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            baselineTasks: [],
            isFetchingTasks: false,
            lastClickedEvent: null,
            dateChangeDeltaDays: 0,
            lastSearchTime: new Date(),
            chartData: {
                data: [],
                links: [],
                markers: []
            },
            showChangeStartDateModal: false,
            showChangeDaysOutModal: false,
            contingentMissingLocations: [],
            isLinking: false,
            lastLinkInfo: {
                task: null,
                contingentId: null
            }
        }
    }

    componentDidMount() {
        this.getTasksForOpeningDate();
    }

    componentDidUpdate(prevProps) {
        // need to fire when filters or opening date changes
        if (
            (this.props.lastFilterChange && this.props.lastFilterChange !== prevProps.lastFilterChange)
            || (this.props.openingDate && this.props.openingDate !== prevProps.openingDate)
        ) {
            this.getTasksForOpeningDate();
        }
    }

    getTasksForOpeningDate = async () => {
        try {

            this.setState({ isFetchingTasks: true });
            const request = {
                ...this.props.searchParams
            };

            request.openingDate = this.props.openingDate;

            request.departments = this.props.searchParams.departments.map(x => x.id);
            request.baselineJobTitles = this.props.searchParams.baselineJobTitles.map(x => x.id);
            request.communities = this.props.searchParams.communities.map(x => x.id);
            request.phases = this.props.searchParams.phases.map(x => x.id);
            request.disciplines = this.props.searchParams.disciplines.map(x => x.id);

            const result = await this.props.baselineTaskActions.searchForGanttTasks(request);
            const payload = result.payload;
            if (result.type === types.SEARCH_BASELINE_TASK_GANTT_SUCCESS) {
                this.setState({
                    isFetchingTasks: false,
                    baselineTasks: result.payload.tasks
                }, this.convertTasksToGanttData(result.payload.tasks));
            } else if (result.type === types.SEARCH_BASELINE_TASK_GANTT_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        } finally {
            this.setState({ isFetchingTasks: false });
        }
    }

    updateTaskDates = async (taskId, startDate, targetDate) => {
        try {
            // updates the start and end dates of a task in the db
            const response = await this.props.baselineTaskActions.updateDates(taskId, startDate, targetDate, Moment.utc(this.props.openingDate));
            if (response.type === types.UPDATE_BASELINE_TASKS_DATES_SUCCESS) {
                Notification("success", "", "Task updated");
                this.getTasksForOpeningDate(false);

            } else if (response.type === types.UPDATE_BASELINE_TASKS_DATES_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ lastSearchTime: new Date() });
            }

        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    updateContingent = async (taskId, contingentId) => {
        try {
            // contingentId will be null on removal of gantt chart link
            const response = await this.props.baselineTaskActions.updateContingent(taskId, contingentId);
            if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {

                Notification("success", "", "Task updated");
                this.setState({ isLinking: false });
                this.getTasksForOpeningDate(false);

            } else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLinking: false });
                this.getTasksForOpeningDate(false);
            }

        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    convertTasksToGanttData = (taskObjects) => {
        const retval = {
            data: [],
            links: []
        };

        taskObjects.forEach((baselineTask) => {

            let eventText = baselineTask.name;
            if (baselineTask.isMilestone) {
                eventText = "&#x25C6; " + baselineTask.name;
            }

            if (baselineTask.includeCertificationCheckbox) {
                eventText = "&#x2611; " + eventText;
            }

            let eventTask = {
                id: baselineTask.id,
                text: eventText,
                eh_task: baselineTask,
                eh_showStatus: false
            };

            // if the task has no start date, make the event.start the task.targetDate
            if (!baselineTask.startDate) {
                eventTask.eh_noStartDate = true;
                eventTask.duration = 1;
                eventTask.start_date = Moment.utc(baselineTask.targetDate).format(EVENT_DATE_FORMAT);

            } else {
                eventTask.eh_noStartDate = false;
                const startDate = Moment.utc(baselineTask.startDate);
                const targetDate = Moment.utc(baselineTask.targetDate);

                // add 1 to duration so the target date is included visually
                eventTask.duration = Moment.duration(targetDate.diff(startDate)).as("days") + 1;

                eventTask.start_date = startDate.format(EVENT_DATE_FORMAT);
            }

            // see if this task has any contingents and add a link
            const children = taskObjects.filter(m => m.contingentId === baselineTask.id);
            if (children && children.length > 0) {
                children.forEach(function (child) {
                    retval.links.push({
                        id: child.id,
                        source: child.id,
                        target: baselineTask.id,
                        type: 0
                    });
                });
            }

            retval.data.push(eventTask);
        });

        // setting the lastSearchTime here will reload the gantt chart. 
        const newDate = new Date();
        this.setState({ chartData: retval, lastSearchTime: newDate });
    }

    onSelectTask = (taskId) => {
        this.props.onSelectTask(taskId);
    }

    onMoveTask = (taskId, deltaDays) => {
        const movingTask = this.state.baselineTasks.find(m => m.id === taskId);

        if (movingTask.contingentId) {
            // pre-req is moving
            let movingTaskEndDate = Moment(movingTask.targetDate);
            movingTaskEndDate = movingTaskEndDate.add(deltaDays, "days");
            const successorTask = this.state.baselineTasks.find(m => m.id === movingTask.contingentId);

            if (!successorTask) {
                Notification("error", "", "Child task not found.");
                this.undoChanges();
            }

            const childStartDate = (successorTask.startDate) ? Moment(successorTask.startDate) : Moment(successorTask.targetDate);

            const diffDays = Moment.duration(movingTaskEndDate.diff(childStartDate)).as("days");

            if (diffDays > 1) {
                Notification("error", "", "A child task cannot start or end before the start of its contingent");
                this.undoChanges();
                return;
            }
        } else {
            // successorTask is moving
            const prereqTask = this.state.baselineTasks.find(m => m.contingentId == movingTask.id);

            if (prereqTask) {
                const parentTargetDate = Moment(prereqTask.targetDate);
                let movingTaskStartDate = (movingTask.startDate) ? Moment(movingTask.startDate) : Moment(movingTask.targetDate);
                movingTaskStartDate = movingTaskStartDate.add(deltaDays, "days");
                const diffDays = Moment.duration(movingTaskStartDate.diff(parentTargetDate)).as("days");

                if (diffDays <= 0) {
                    Notification("error", "", "A Child task cannot start or end before the end of its contingent");
                    this.undoChanges();
                    return;
                }
            }
        }

        if (movingTask.targetStaticDate) {
            // allows for changing both start and target dates, but contains logic to only allow target date change if user has access
            this.setState({
                lastClickedEvent: movingTask,
                showChangeStartDateModal: true,
                dateChangeDeltaDays: deltaDays
            });
        } else {
            // days out modal
            this.setState({
                lastClickedEvent: movingTask,
                showChangeDaysOutModal: true,
                dateChangeDeltaDays: deltaDays
            });
        }
    }


    onResizeTask = (taskId, originalStart, originalTarget, updatedStart, updatedTarget) => {
        const movingTask = this.state.baselineTasks.find(m => m.id === taskId);

        if (movingTask.contingentId) {
            // pre-req is moving
            const childTask = this.state.baselineTasks.find(m => m.id === movingTask.contingentId);

            if (!childTask) {
                Notification("error", "", "Child task not found.");
                this.undoChanges();
            }

            const childTaskStartDate = (childTask.startDate) ? Moment(childTask.startDate) : Moment(childTask.targetDate);
            const diffDays = Moment.duration(updatedTarget.diff(childTaskStartDate)).as("days");

            if (diffDays >= 1) {
                Notification("error", "", "A child task cannot start or end before the start of its parent");
                this.undoChanges();
                return;
            }
        } else {
            // successorTask is moving
            const prereqTask = this.state.baselineTasks.find(m => m.contingentId == movingTask.id);
            if (prereqTask) {
                const parentTask = this.state.baselineTasks.find(m => m.contingentId === movingTask.id);
                const parentTargetDate = Moment(parentTask.targetDate);
                const diffDays = Moment.duration(updatedStart.diff(parentTargetDate)).as("days");

                if (diffDays < 0) {
                    Notification("error", "", "A child task cannot start or end before the start of its parent");
                    this.undoChanges();
                    return;
                }
            }
        }

        // figure out which end was resized - only one side can change at a time
        // != does not work so check duration diff
        if (Moment.duration(updatedStart.diff(originalStart)).as("days") !== 0) {
            // anyone can change the start date
            this.updateTaskDates(taskId, updatedStart, originalTarget);

        } else if (Moment.duration(updatedTarget.diff(originalTarget)).as("days")) {
            this.updateTaskDates(taskId, originalStart, updatedTarget);
        }

    }

    onCloseChangeStartDateModal = () => {
        this.undoChanges();
        this.setState({ showChangeStartDateModal: false });
    }

    onCompleteChangeStartDateModal = (startDate, targetDate) => {
        this.setState({ showChangeStartDateModal: false }, () => {
            const baselineTask = this.state.lastClickedEvent;
            this.updateTaskDates(baselineTask.id, startDate, targetDate);
        });
    }

    onCompleteChangeDaysOutModal = () => {
        this.setState({ showChangeDaysOutModal: false }, () => {
            const baselineTask = this.state.lastClickedEvent;
            const newStartDate = Moment(baselineTask.startDate).add(this.state.dateChangeDeltaDays, "days");
            const newTargetDate = Moment(baselineTask.targetDate).add(this.state.dateChangeDeltaDays, "days");
            this.updateTaskDates(baselineTask.id, newStartDate, newTargetDate);
        });
    }

    onCloseChangeDaysOutModal = () => {
        this.undoChanges();
        this.setState({ showChangeDaysOutModal: false });
    }

    onRemoveLink = (taskId) => {
        const updatedTask = this.state.baselineTasks.find(m => m.id === parseInt(taskId));
        let newContingentInfo = {
            task: updatedTask,
            contingentId: null
        };
        this.setState({ lastLinkInfo: newContingentInfo }, () => this.updateContingent(taskId, null));
    }

    onAddLink = (sourceId, targetId, type) => {
        // contingent = successor
        // preventive measure to stop dupes - stupid thing fires like 4-5 times
        if (!this.state.isLinking) {
            const sourceTask = this.state.baselineTasks.find(m => m.id === parseInt(sourceId));
            const targetTask = this.state.baselineTasks.find(m => m.id == parseInt(targetId));

            this.setState({ isLinking: true }, () => {

                let newContingentInfo = {
                    task: null,
                    contingentId: null
                };

                if (type === GANTT_LINK_TYPES.StartToStart || type === GANTT_LINK_TYPES.FinishToStart) {

                    // A task can have multiple pre-req tasks; but a task can only be a pre-req for one task.
                    if (sourceTask.contingentId) {
                        Notification("error", "", "A task can only be a prereq of 1 task");
                        this.undoChanges();
                        return;
                    }

                    const parentTargetDate = Moment(sourceTask.targetDate);
                    const childTargetDate = Moment(targetTask.targetDate);

                    const diffDays = Moment.duration(parentTargetDate.diff(childTargetDate)).as("days");

                    if (diffDays > 0) {
                        Notification("error", "", "A contingent task cannot start or end before the start of the other task");
                        this.undoChanges();
                        return;
                    }

                    newContingentInfo.task = sourceTask;
                    newContingentInfo.contingentId = targetTask.id;

                } else { // StartToFinish or FinishToFinish

                    // A task can have multiple pre-req tasks; but a task can only be a pre-req for one task.
                    if (targetTask.contingentId) {
                        Notification("error", "", "A task can only be a prereq of 1 task");
                        this.undoChanges();
                        return;
                    }

                    const parentTargetDate = Moment(targetTask.targetDate);
                    const childTargetDate = Moment(sourceTask.targetDate);

                    const diffDays = Moment.duration(childTargetDate.diff(parentTargetDate)).as("days");

                    if (diffDays < 0) {
                        Notification("error", "", "A contingent task cannot start or end before the start of the other task");
                        this.undoChanges();
                        return;
                    }

                    newContingentInfo.task = targetTask;
                    newContingentInfo.contingentId = sourceTask.id;

                }
                this.setState({ lastLinkInfo: newContingentInfo }, () => {
                    this.updateContingent(newContingentInfo.task.id, newContingentInfo.contingentId);
                });
            })
        }
    }

    undoChanges = () => {
        // resets the gantt chart without having to hit the api
        this.setState({ isLinking: false }, () => {
            this.convertTasksToGanttData(this.state.baselineTasks);
        });
    }

    getHeight = () => {
        // makes the chart fill the rest of the page with enough space below it to match the margins on the sides
        return window.innerHeight - 175;
    }

    render() {
        return (
            <div className="gantt-chart-view">
                <LoadingIndicator isLoading={this.state.isFetchingTasks}>
                    <GanttChart
                        chartData={this.state.chartData}
                        height={this.getHeight()}
                        onMoveTask={this.onMoveTask}
                        onResizeTask={this.onResizeTask}
                        onRemoveLink={this.onRemoveLink}
                        onAddLink={this.onAddLink}
                        onSelectTask={this.onSelectTask}
                        lastSearchTime={this.state.lastSearchTime}
                        zoomLevel={this.props.zoomLevel}
                        exportToPDF={false}
                        startOnMonday={false}
                        isTaskDependenciesActive={this.props.isTaskDependenciesActive}
                        isStartDateActive={this.props.isStartDateActive}
                    />
                </LoadingIndicator>
                {this.state.showChangeStartDateModal &&
                    <ChangeStartDateModal
                        task={this.state.lastClickedEvent}
                        onCancel={this.onCloseChangeStartDateModal}
                        onComplete={this.onCompleteChangeStartDateModal}
                        deltaDays={this.state.dateChangeDeltaDays}
                        userCanChangeTargetDate={true}
                    />
                }
                {this.state.showChangeDaysOutModal &&
                    <ChangeDaysOutModal
                        task={this.state.lastClickedEvent}
                        deltaDays={this.state.dateChangeDeltaDays}
                        onComplete={this.onCompleteChangeDaysOutModal}
                        onCancel={this.onCloseChangeDaysOutModal}
                    />
                }
            </div>
        );
    }
}

GanttChartView.propTypes = {
    subscriptionId: PropTypes.string.isRequired,
    searchParams: PropTypes.object.isRequired,
    onSelectTask: PropTypes.func.isRequired,
    baselineTaskActions: PropTypes.object.isRequired,
    lastFilterChange: PropTypes.instanceOf(Date).isRequired,
    openingDate: PropTypes.string,
    zoomLevel: PropTypes.number.isRequired,
    isTaskDependenciesActive: PropTypes.bool.isRequired,
    isStartDateActive: PropTypes.bool.isRequired
};

export default GanttChartView;
